import { BaseOptionType } from 'components/Select';
import { TMSEquipmentType } from 'types/Load';
import { MaybeUndef, Undef } from 'types/UtilityTypes';

export type PlsEquipmentOption = BaseOptionType & {
  value: string;
  label: string;
  // PLS catalog name, trimmed (e.g. "Flatbed 48'").
  name: string;
  // Drumkit class the backend derives for the code (e.g. "FLATBED"); empty when not sent.
  transportType: string;
};

// Label is PLS's own catalog name followed by the code ("Flatbed 48' (F48)").
// The code alone is shown when PLS names the record with its code ("BARGE").
export function plsEquipmentLabel(code: string, name: string): string {
  if (!name || name.toUpperCase() === code.toUpperCase()) {
    return code;
  }
  return `${name} (${code})`;
}

// Builds one option per PLS catalog code. Invariants:
//   - the option value is the catalog code exactly as PLS sends it (codes can contain spaces);
//   - codes are unique: PLS lists a few codes twice (TPDBL), the first record wins, because
//     Radix Select needs unique values;
//   - records without a code are dropped.
export function buildPlsEquipmentOptions(
  catalog: MaybeUndef<TMSEquipmentType[]>
): PlsEquipmentOption[] {
  const options: PlsEquipmentOption[] = [];
  const seen = new Set<string>();

  for (const equipment of Array.isArray(catalog) ? catalog : []) {
    const code = String(equipment?.id ?? '').trim();
    if (!code || seen.has(code)) {
      continue;
    }
    seen.add(code);

    const name = String(equipment?.name ?? '').trim();
    options.push({
      value: code,
      label: plsEquipmentLabel(code, name),
      name: name || code,
      transportType: String(equipment?.transportType ?? '').trim(),
    });
  }

  return options;
}

// Option for a saved code the catalog does not list, so Load View still shows the code PLS has.
export function plsUnlistedEquipmentOption(code: string): PlsEquipmentOption {
  return { value: code, label: code, name: code, transportType: '' };
}

// Search matches the catalog name, the code and the Drumkit class. Every whitespace-separated
// term must appear in one of them, so "van 53", "V53" and "flatbed" all find their flavors.
export function plsEquipmentMatchesSearch(
  input: string,
  option: Undef<BaseOptionType>
): boolean {
  if (!option) {
    return false;
  }
  const terms = input.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) {
    return true;
  }

  const haystack = [option.label, option.value, option.transportType]
    .map((part) => String(part ?? '').toLowerCase())
    .join(' ');
  return terms.every((term) => haystack.includes(term));
}

// Resolves a suggested or saved value to a catalog code. Order: exact code, code ignoring case,
// then catalog name ignoring case. Returns undefined when nothing matches, so the caller leaves
// the value alone.
export function resolvePlsEquipmentCode(
  value: string,
  options: PlsEquipmentOption[]
): Undef<string> {
  const target = value.trim();
  if (!target) {
    return undefined;
  }
  const exact = options.find((option) => option.value === target);
  if (exact) {
    return exact.value;
  }
  const lowered = target.toLowerCase();
  const byCode = options.find(
    (option) => option.value.toLowerCase() === lowered
  );
  if (byCode) {
    return byCode.value;
  }
  return options.find((option) => option.name.toLowerCase() === lowered)?.value;
}

// Quiet secondary text for the class: "STRAIGHT TRUCK" reads as "Straight truck".
export function plsEquipmentClassLabel(transportType: string): string {
  const lowered = transportType.trim().toLowerCase();
  return lowered ? lowered.charAt(0).toUpperCase() + lowered.slice(1) : '';
}
